import React from 'react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Pagination } from '../../../components/ui/pagination';
import { SortableHeader } from '@/components/ui/sortable-header';
import { useTableData } from '@/hooks/useTableData';
import { Edit, Trash2, Play, Pause, Tag } from 'lucide-react';
import { Promotion } from '../types';

interface PromotionsTableProps {
  promotions: Promotion[];
  onEdit: (promotion: Promotion) => void;
  onDelete: (id: number) => void;
  onToggleStatus: (id: number, currentStatus: boolean) => void;
}

type SortField = 'name' | 'type' | 'status' | 'usageCount';

const getPromotionTypeLabel = (type: string) => {
  const labels = {
    percentage: 'Porcentual',
    fixed: 'Fijo',
    combo: 'Combo',
    bxgy: 'Compra X Lleva Y'
  };
  return labels[type as keyof typeof labels] || type;
};

const getPromotionStatus = (promotion: Promotion) => {
  const now = new Date();
  if (!promotion.isActive) return 'inactive';
  if (promotion.endDate && now > new Date(promotion.endDate)) return 'expired';
  if (promotion.startDate && now < new Date(promotion.startDate)) return 'scheduled';
  return 'active';
};

const getStatusBadge = (status: string) => {
  const variants = {
    active: 'bg-green-100 text-green-800',
    inactive: 'bg-gray-100 text-gray-800',
    scheduled: 'bg-blue-100 text-blue-800',
    expired: 'bg-red-100 text-red-800'
  };
  
  const labels = {
    active: 'Activo',
    inactive: 'Inactivo',
    scheduled: 'Programado',
    expired: 'Expirado'
  };
  
  return {
    variant: variants[status as keyof typeof variants] || variants.inactive,
    label: labels[status as keyof typeof labels] || status
  };
};

export const PromotionsTable: React.FC<PromotionsTableProps> = ({
  promotions,
  onEdit,
  onDelete,
  onToggleStatus
}) => {
  // Configuración del hook reutilizable
  const fieldLabels: Record<SortField, string> = {
    name: 'Nombre',
    type: 'Tipo',
    status: 'Estado',
    usageCount: 'Uso'
  };

  // Extractor personalizado para campos complejos
  const sortValueExtractor = (promotion: Promotion, field: SortField): any => {
    switch (field) {
      case 'name':
        return promotion.name.toLowerCase();
      case 'type':
        return promotion.type;
      case 'status':
        const status = getPromotionStatus(promotion);
        // Orden: active > scheduled > inactive > expired
        const statusOrder = { active: 4, scheduled: 3, inactive: 2, expired: 1 };
        return statusOrder[status as keyof typeof statusOrder] || 0;
      case 'usageCount':
        return promotion.usageCount || 0;
      default:
        return promotion.name;
    }
  };

  // Usar el hook reutilizable
  const tableData = useTableData<Promotion, SortField>(
    {
      data: promotions,
      defaultSortField: 'name',
      defaultSortDirection: 'asc',
      itemsPerPage: 10,
      sortValueExtractor
    },
    fieldLabels
  );
  if (promotions.length === 0) {
    return (
      <div className="text-center py-12">
        <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No hay promociones</h3>
        <p className="text-gray-500">No se encontraron promociones con los filtros aplicados.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortableHeader
                field="name"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Nombre
              </SortableHeader>
              <SortableHeader
                field="type"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Tipo
              </SortableHeader>
              <SortableHeader
                field="status"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Estado
              </SortableHeader>
              <SortableHeader
                field="usageCount"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Uso
              </SortableHeader>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tableData.pagination.paginatedData.map((promotion) => {
          const status = getPromotionStatus(promotion);
          const statusBadge = getStatusBadge(status);
          
              return (
                <tr key={promotion.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{promotion.name}</div>
                      <div className="text-sm text-gray-500">{promotion.description}</div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <Badge variant="outline">
                      {getPromotionTypeLabel(promotion.type)}
                    </Badge>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <Badge className={statusBadge.variant}>
                      {statusBadge.label}
                    </Badge>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className="text-sm font-medium text-gray-900">{promotion.usageCount}</span>
                    <span className="text-xs text-gray-500 ml-1">veces</span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <div className="flex justify-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onToggleStatus(promotion.id, promotion.isActive)}
                        className="text-xs"
                      >
                        {promotion.isActive ? (
                          <Pause className="w-3 h-3 mr-1" />
                        ) : (
                          <Play className="w-3 h-3 mr-1" />
                        )}
                        {promotion.isActive ? 'Pausar' : 'Activar'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(promotion)}
                        className="text-xs"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDelete(promotion.id)}
                        className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Sort info */}
      <div className="mt-2 text-sm text-gray-500">
        Ordenado por: <span className="font-medium">
          {tableData.sortInfo.label}
        </span> ({tableData.sortInfo.direction === 'asc' ? 'Ascendente' : 'Descendente'})
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={tableData.pagination.currentPage}
        totalPages={tableData.pagination.totalPages}
        onPageChange={tableData.pagination.goToPage}
        showingFrom={tableData.pagination.showingFrom}
        showingTo={tableData.pagination.showingTo}
        totalItems={tableData.pagination.totalItems}
        itemsPerPage={tableData.pagination.itemsPerPage}
        onItemsPerPageChange={tableData.pagination.setItemsPerPage}
      />
    </div>
  );
};